import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faRecycle,
  faLocationDot,
  faEarthEurope,
  faMobileScreen,
  faCircleCheck
} from '@fortawesome/free-solid-svg-icons';
import Navigation from './components/Navigation';
import { ToastContainer } from './components/Toast';
import SEO from './components/SEO';
import Breadcrumbs from './components/Breadcrumbs';

// Lazy load page components for better performance
const MapPage = lazy(() => import('./pages/MapPage'));
const StationDetailPage = lazy(() => import('./pages/StationDetailPage'));
const KommunDetailPage = lazy(() => import('./pages/KommunDetailPage'));
const ReportIssuePage = lazy(() => import('./pages/ReportIssuePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const API_URL = process.env.REACT_APP_API_URL || '/data';

function HomePage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/counts`)
      .then(response => {
        setStats(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching counts:', error);
        setLoading(false);
        // Show error toast
        window.dispatchEvent(
          new CustomEvent('show-toast', {
            detail: {
              message: 'Kunde inte ladda statistik. Försök igen senare.',
              type: 'error',
              duration: 5000
            }
          })
        );
      });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      <SEO
        title="Hem"
        description="Hitta återvinningsstationer och sorteringsguide för alla Sveriges 290 kommuner. Gratis och alltid uppdaterat."
        keywords="återvinning, återvinningsstation, ÅVC, sorteringsguide, avfall, miljö, Sverige"
      />
      {/* Hero Section */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-5xl font-bold tracking-tight text-neutral-900 sm:text-6xl">
            Återvinningsguiden
          </h1>
          <p className="mt-6 text-xl text-neutral-600 max-w-2xl mx-auto">
            Sveriges nationella återvinningsportal. Hitta återvinningsstationer, sorteringsguider och information om återvinning i din kommun.
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <Link
              to="/kommuner"
              className="btn-primary text-lg"
            >
              Hitta din kommun →
            </Link>
            <Link
              to="/sorteringsguide"
              className="btn-secondary text-lg"
            >
              Sorteringsguide
            </Link>
          </div>
          <p className="mt-4 text-sm text-neutral-500">
            100% Gratis • Alla Sveriges kommuner • Alltid uppdaterat
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-16 grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-3 animate-fadeIn mobile-safe">
            <div className="card text-center bg-gradient-to-br from-primary-500 to-primary-600 text-white border-0 hover:shadow-2xl transition-shadow duration-200">
              <div className="text-4xl font-bold">{stats.kommuner}</div>
              <div className="mt-2 text-lg opacity-90">Kommuner</div>
            </div>
            <div className="card text-center bg-gradient-to-br from-secondary-500 to-secondary-600 text-white border-0 hover:shadow-2xl transition-shadow duration-200">
              <div className="text-4xl font-bold">{stats.stationer}</div>
              <div className="mt-2 text-lg opacity-90">Återvinningsstationer</div>
            </div>
            <div className="card text-center bg-gradient-to-br from-neutral-700 to-neutral-800 text-white border-0 hover:shadow-2xl transition-shadow duration-200">
              <div className="text-4xl font-bold">{stats.avfallstyper}</div>
              <div className="mt-2 text-lg opacity-90">Avfallstyper</div>
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mt-24">
          <div className="grid grid-cols-1 gap-4 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3 mobile-safe">
            {/* Feature 1 */}
            <div className="card">
              <div className="text-primary-500 text-4xl mb-4">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                290 Svenska kommuner
              </h3>
              <p className="text-neutral-600">
                Komplett information om återvinning i alla Sveriges kommuner. Hitta återvinningsstationer nära dig.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="card">
              <div className="text-primary-500 text-4xl mb-4">
                <FontAwesomeIcon icon={faRecycle} />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Sorteringsguide
              </h3>
              <p className="text-neutral-600">
                Lär dig hur du sorterar olika typer av avfall korrekt. Från matavfall till elektronik.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="card">
              <div className="text-primary-500 text-4xl mb-4">
                <FontAwesomeIcon icon={faLocationDot} />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Hitta närmaste station
              </h3>
              <p className="text-neutral-600">
                Sök efter återvinningsstationer i din kommun med öppettider och kontaktinformation.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="card">
              <div className="text-primary-500 text-4xl mb-4">
                <FontAwesomeIcon icon={faEarthEurope} />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Miljövänligt
              </h3>
              <p className="text-neutral-600">
                Bidra till en bättre miljö genom korrekt återvinning. Varje insats räknas.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="card">
              <div className="text-primary-500 text-4xl mb-4">
                <FontAwesomeIcon icon={faMobileScreen} />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Mobilvänlig
              </h3>
              <p className="text-neutral-600">
                Sök information var du än är. Optimerad för både mobil och desktop.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="card">
              <div className="text-primary-500 text-4xl mb-4">
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Alltid gratis
              </h3>
              <p className="text-neutral-600">
                Ingen registrering krävs. All information är tillgänglig för alla, helt gratis.
              </p>
            </div>
          </div>
        </div>

        {/* SEO Keywords Section */}
        <div className="mt-24 bg-white rounded-2xl p-4 sm:p-8 shadow-sm border border-neutral-200 mobile-safe">
          <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4 sm:mb-6 text-center">
            Allt du behöver veta om återvinning i Sverige
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm text-neutral-600">
            <div>• Återvinningsstation</div>
            <div>• Återvinning Sverige</div>
            <div>• Sorteringsguide</div>
            <div>• Återvinningscentral</div>
            <div>• Miljöstation</div>
            <div>• Sopsortering</div>
            <div>• Matavfall</div>
            <div>• Elektronikåtervinning</div>
            <div>• Farligt avfall</div>
            <div>• Återvinning tips</div>
            <div>• Kommunal återvinning</div>
            <div>• Hållbar utveckling</div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-24 text-center">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Börja återvinna smartare idag
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            Hitta din närmaste återvinningsstation och lär dig sortera rätt.
          </p>
          <Link to="/kommuner" className="btn-primary text-lg inline-flex">
            Utforska kommuner →
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-24 border-t border-neutral-200 bg-neutral-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-neutral-600">
            <p>© 2025 Återvinningsguiden - Sveriges nationella återvinningsportal</p>
            <div className="mt-4 space-x-6">
              <Link to="/rapportera-fel" className="hover:text-primary-600">Rapportera fel</Link>
              <span className="hover:text-primary-600 cursor-pointer">Integritetspolicy</span>
              <span className="hover:text-primary-600 cursor-pointer">Användarvillkor</span>
              <span className="hover:text-primary-600 cursor-pointer">Kontakt</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function KommunerPage() {
  const navigate = useNavigate();
  const [kommuner, setKommuner] = useState([]);
  const [allKommuner, setAllKommuner] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteResults, setAutocompleteResults] = useState([]);

  useEffect(() => {
    // Fetch all kommuner once for autocomplete
    axios.get(`${API_URL}/kommuner?limit=350`)
      .then(response => {
        // Handle new API format: {success: true, data: [...], pagination: {...}}
        let data = response.data.data || [];
        // Ensure data is an array
        if (!Array.isArray(data)) {
          console.error('API returned non-array data:', data);
          data = [];
        }
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name, 'sv-SE'));
        setAllKommuner(sortedData);
        setKommuner(sortedData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching kommuner:', error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (search.trim().length > 0) {
      const filtered = allKommuner.filter(k =>
        k.name.toLowerCase().includes(search.toLowerCase()) ||
        (k.lan && k.lan.toLowerCase().includes(search.toLowerCase()))
      );
      setAutocompleteResults(filtered.slice(0, 10));
      setShowAutocomplete(true);
      setKommuner(filtered);
    } else {
      setShowAutocomplete(false);
      setKommuner(allKommuner);
    }
  }, [search, allKommuner]);

  const handleSelectKommun = (kommun) => {
    // Navigate to kommun detail page when selected from autocomplete
    navigate(`/kommun/${kommun.slug}`);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <SEO
        title="Kommuner"
        description="Sök bland alla Sveriges 290 kommuner. Hitta återvinningsstationer, öppettider och kontaktinformation."
        keywords="kommuner, Sverige, återvinning, ÅVC, återvinningscentral"
      />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Breadcrumbs />
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-neutral-900 mb-2">
            Svenska Kommuner
          </h1>
          <p className="text-lg text-neutral-600">
            Sök bland alla Sveriges {kommuner.length > 0 ? kommuner.length : '290'} kommuner
          </p>
        </div>

        <div className="mb-8 relative max-w-xl">
          <input
            type="text"
            placeholder="Sök kommun..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => search && setShowAutocomplete(true)}
            onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
            className="input-base w-full"
            aria-autocomplete="list"
            aria-controls="kommun-autocomplete"
            aria-expanded={showAutocomplete}
          />
          {showAutocomplete && autocompleteResults.length > 0 && (
            <div
              id="kommun-autocomplete"
              role="listbox"
              data-testid="kommun-autocomplete"
              className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              {autocompleteResults.map((kommun) => (
                <button
                  key={kommun.id}
                  onClick={() => handleSelectKommun(kommun)}
                  data-testid={`autocomplete-option-${kommun.slug}`}
                  role="option"
                  className="w-full text-left px-4 py-2 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none border-b border-neutral-100 last:border-b-0"
                >
                  <div className="font-medium text-neutral-900">{kommun.name}</div>
                  {kommun.lan && (
                    <div className="text-sm text-neutral-600">{kommun.lan}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 transition-opacity duration-300">
            {kommuner.length > 0 ? (
              kommuner.map(kommun => (
                <Link
                  key={kommun.id}
                  to={`/kommun/${kommun.slug}`}
                  data-testid={`kommun-card-${kommun.slug}`}
                  data-kommun-name={kommun.name}
                  className="card hover:shadow-lg transition-shadow"
                  aria-label={`Visa information om ${kommun.name} kommun`}
                >
                  <h3 className="text-xl font-semibold text-neutral-900 mb-3">
                    {kommun.name}
                  </h3>
                  {kommun.lan && (
                    <p className="text-sm text-neutral-600 mb-2">
                      <strong>Län:</strong> {kommun.lan}
                    </p>
                  )}
                  {kommun.befolkning && (
                    <p className="text-sm text-neutral-600 mb-2">
                      <strong>Befolkning:</strong> {kommun.befolkning.toLocaleString('sv-SE')}
                    </p>
                  )}
                  <div className="mt-3 pt-3 border-t border-neutral-200">
                    <span className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                      Visa återvinningsstationer →
                    </span>
                  </div>
                </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-neutral-600">Inga kommuner hittades</p>
                {search && (
                  <p className="text-neutral-500 mt-2">Prova att söka efter något annat</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SorteringsGuidePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const searchParam = search ? `&search=${search}` : '';
    axios.get(`${API_URL}/sorteringsguide?limit=100${searchParam}`)
      .then(response => {
        // Handle both old array format and new paginated format
        let data = response.data.data || response.data;
        // Ensure data is an array
        if (!Array.isArray(data)) {
          console.error('API returned non-array data:', data);
          data = [];
        }
        // Sort alphabetically by namn
        const sortedData = data.sort((a, b) => a.namn.localeCompare(b.namn, 'sv-SE'));
        setItems(sortedData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching guide:', error);
        setLoading(false);
      });
  }, [search]);

  return (
    <div className="min-h-screen bg-neutral-50">
      <SEO
        title="Sorteringsguide"
        description="Lär dig hur du sorterar ditt avfall korrekt. Sök bland hundratals avfallstyper och lär dig var de ska sorteras."
        keywords="sorteringsguide, avfall, sopor, återvinning, miljö, källsortering"
      />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Breadcrumbs />
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-neutral-900 mb-2">
            Sorteringsguide
          </h1>
          <p className="text-lg text-neutral-600">
            Lär dig hur du sorterar olika typer av avfall korrekt
          </p>
        </div>

        <div className="mb-8">
          <input
            type="text"
            placeholder="Sök vad du vill sortera..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-base max-w-xl"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6">
            {[...Array(4)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 transition-opacity duration-300">
            {items.length > 0 ? (
              items.map(item => (
                <Link key={item.id} to={`/sorteringsguide/${item.slug}`} className="card hover:shadow-lg transition-shadow duration-200">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                        {item.namn}
                      </h3>
                      {item.avfallstyp_namn && (
                        <span
                          className="inline-block px-3 py-1 rounded-full text-sm font-medium text-white mb-3"
                          style={{ backgroundColor: item.farg || '#999' }}
                        >
                          {item.avfallstyp_namn}
                        </span>
                      )}
                      {item.beskrivning && (
                        <p className="text-neutral-600 mb-3">{item.beskrivning}</p>
                      )}
                      {item.tips && (
                        <div className="mt-4 p-4 bg-primary-50 border-l-4 border-primary-500 rounded">
                          <p className="text-sm text-primary-900">
                            <strong>💡 Tips:</strong> {item.tips}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-neutral-600">Inga sorteringsguide-objekt hittades</p>
                {search && (
                  <p className="text-neutral-500 mt-2">Prova att söka efter något annat</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Sorteringsguide Detail Page
function SorteringsGuideDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/sorteringsguide/${slug}`)
      .then(response => {
        let item = response.data;

        // Parse JSON fields
        try {
          if (item.tillat && typeof item.tillat === 'string') {
            item.tillat = JSON.parse(item.tillat);
          }
          if (item.ej_tillat && typeof item.ej_tillat === 'string') {
            item.ej_tillat = JSON.parse(item.ej_tillat);
          }
          if (item.tips_json && typeof item.tips_json === 'string') {
            item.tips_json = JSON.parse(item.tips_json);
          }
          if (item.ikoner && typeof item.ikoner === 'string') {
            item.ikoner = JSON.parse(item.ikoner);
          }
          if (item.farger && typeof item.farger === 'string') {
            item.farger = JSON.parse(item.farger);
          }
        } catch (e) {
          console.error('Error parsing JSON fields:', e);
        }

        setItem(item);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching guide detail:', error);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-neutral-600">Laddar...</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h1 className="text-3xl font-bold text-neutral-900 mb-4">Artikeln hittades inte</h1>
            <Link to="/sorteringsguide" className="btn-primary">
              Tillbaka till sorteringsguiden
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-neutral-100">
      <SEO
        title={item.meta_title || `${item.namn} - Sorteringsguide`}
        description={item.meta_description || item.sammanfattning || item.beskrivning}
        keywords={`${item.namn}, sortering, återvinning, avfall`}
      />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <Breadcrumbs />

        {/* Hero Section */}
        <div className="mb-10 bg-white rounded-2xl shadow-lg overflow-hidden">
          {item.bild_url && (
            <div className="relative h-80">
              <img
                src={`/${item.bild_url}`}
                alt={item.namn}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <h1 className="absolute bottom-6 left-6 text-5xl font-bold text-white drop-shadow-lg">
                {item.namn}
              </h1>
            </div>
          )}
          {!item.bild_url && (
            <div className="p-8">
              <h1 className="text-5xl font-bold text-neutral-900">{item.namn}</h1>
            </div>
          )}
        </div>

        {/* Summary - Highlighted callout */}
        {item.sammanfattning && (
          <div className="mb-10 p-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-xl text-white">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-3xl">
                ♻️
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-3">Så här gör du</h2>
                <p className="text-xl leading-relaxed text-white/95">{item.sammanfattning}</p>
              </div>
            </div>
          </div>
        )}

        {/* Two-column layout for Tillåt/Ej tillåt */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          {/* Tillåt */}
          {item.tillat && item.tillat.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 border-t-4 border-green-500">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">✓</span>
                </div>
                <h2 className="text-2xl font-bold text-green-900">Ja tack!</h2>
              </div>
              <ul className="space-y-3">
                {item.tillat.map((tillat_item, index) => (
                  <li key={index} className="flex items-start gap-3 p-3 hover:bg-green-50 rounded-lg transition-colors">
                    <span className="text-green-600 text-xl flex-shrink-0 mt-0.5">✓</span>
                    <span className="text-neutral-800 leading-relaxed">{tillat_item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Ej tillåt */}
          {item.ej_tillat && item.ej_tillat.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 border-t-4 border-red-500">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">✗</span>
                </div>
                <h2 className="text-2xl font-bold text-red-900">Nej tack!</h2>
              </div>
              <ul className="space-y-3">
                {item.ej_tillat.map((ej_item, index) => (
                  <li key={index} className="flex items-start gap-3 p-3 hover:bg-red-50 rounded-lg transition-colors">
                    <span className="text-red-600 text-xl flex-shrink-0 mt-0.5">✗</span>
                    <span className="text-neutral-800 leading-relaxed">{ej_item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Varför - Impact section */}
        {item.varfor_text && (
          <div className="mb-10 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg p-8 border-l-4 border-green-500">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl">
                🌍
              </div>
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-green-900 mb-4">Varför det spelar roll</h2>
                <p className="text-lg text-neutral-800 leading-relaxed">{item.varfor_text}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tips - Professional cards */}
        {item.tips_json && item.tips_json.length > 0 && (
          <div className="mb-10">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6 flex items-center gap-3">
              <span className="text-4xl">💡</span>
              Bra att veta
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {item.tips_json.map((tip, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-400 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <span className="text-yellow-500 text-2xl flex-shrink-0">💡</span>
                    <p className="text-neutral-800 leading-relaxed text-lg">{tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Back button */}
        <div className="mt-12 pb-8">
          <Link to="/sorteringsguide" className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-lg shadow-md hover:shadow-lg transition-all text-neutral-700 font-medium">
            <span>←</span>
            <span>Tillbaka till sorteringsguiden</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

// Skeleton loading component
function SkeletonCard() {
  return (
    <div className="card animate-pulse">
      <div className="h-6 bg-neutral-200 rounded w-3/4 mb-4"></div>
      <div className="h-4 bg-neutral-200 rounded w-1/2 mb-2"></div>
      <div className="h-4 bg-neutral-200 rounded w-2/3 mb-2"></div>
      <div className="mt-3 pt-3 border-t border-neutral-200">
        <div className="h-4 bg-neutral-200 rounded w-1/3"></div>
      </div>
    </div>
  );
}

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
        <p className="text-neutral-600">Laddar...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <Navigation />
        <ToastContainer />
        <main id="main-content" role="main">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/kommuner" element={<KommunerPage />} />
              <Route path="/kommun/:slug" element={<KommunDetailPage />} />
              <Route path="/sorteringsguide" element={<SorteringsGuidePage />} />
              <Route path="/sorteringsguide/:slug" element={<SorteringsGuideDetailPage />} />
              <Route path="/hitta-avc" element={<MapPage />} />
              <Route path="/karta" element={<MapPage />} />
              <Route path="/station/:id" element={<StationDetailPage />} />
              <Route path="/rapportera-fel" element={<ReportIssuePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </Router>
  );
}

export default App;
